import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/api';
import OperatingAccessSettings from '../components/OperatingAccessSettings';
import { contextError } from '../utils/operatingContext';
import './OperatingContextsPage.css';

export type OperatingContextResource = {
  resourceType: 'warehouse' | 'kitchen' | 'venue';
  id: number;
  name: string;
  archived: boolean;
  groupIds: number[];
};
export type OperatingContexts = {
  enforcementEnabled: boolean;
  unrestricted: boolean;
  allowLegacyUnscoped: boolean;
  canAdministerGrants: boolean;
  canAdministerPolicy: boolean;
  onDate: string;
  resources: OperatingContextResource[];
};
type BalanceRow = {
  warehouseId: number; warehouseName: string; groupIds: number[];
  productId: number; productName: string; unit: string;
  balance: number; stockIn: number; stockOut: number; unpostedWriteOffQuantity: number;
};
type Report = {
  onDate: string; warehouseIds: number[]; rows: BalanceRow[];
  totals: Omit<BalanceRow, 'warehouseId' | 'warehouseName' | 'groupIds'>[];
};
type ExceptionGroup = { id: number; name: string };
type TransferExceptionCandidate = {
  groupId: number; groupName: string; transferId: number; date: string;
  sourceWarehouseId: number; sourceWarehouseName: string;
  destinationWarehouseId: number; destinationWarehouseName: string;
  items: { productId: number; productName: string; unit: string; quantity: number }[];
  fingerprint: string;
};
export default function OperatingContextsPage() {
  const [contexts, setContexts] = useState<OperatingContexts | null>(null);
  const [selected, setSelected] = useState('');
  const [date, setDate] = useState('');
  const [group, setGroup] = useState('');
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [administration, setAdministration] = useState(false);
  const [exceptionGroups, setExceptionGroups] = useState<ExceptionGroup[]>([]);
  const [exceptionGroupId, setExceptionGroupId] = useState('');
  const [exceptionCandidates, setExceptionCandidates] = useState<TransferExceptionCandidate[]>([]);
  const [exceptionCandidateId, setExceptionCandidateId] = useState('');
  const [exceptionReason, setExceptionReason] = useState('');
  const [exceptionConfirmation, setExceptionConfirmation] = useState('');
  const [exceptionBusy, setExceptionBusy] = useState(false);
  const [exceptionError, setExceptionError] = useState('');
  const [exceptionNotice, setExceptionNotice] = useState('');
  const sequence = useRef(0);
  const refresh = async (onDate?: string) => {
    const current = ++sequence.current;
    setBusy(true); setError(''); setReport(null); setSelected(''); setGroup('');
    try {
      const { data } = await api.get<OperatingContexts>('/operating-contexts', onDate ? { params: { onDate } } : undefined);
      if (current === sequence.current) { setContexts(data); setDate(data.onDate); }
    } catch (failure: unknown) {
      if (current === sequence.current) { setContexts(null); setError(contextError(failure)); }
    } finally { if (current === sequence.current) setBusy(false); }
  };
  useEffect(() => {
    void refresh();
    return () => { sequence.current += 1; };
  }, []);
  useEffect(() => {
    let active = true;
    if (!contexts?.canAdministerPolicy) {
      setExceptionGroups([]);
      return () => { active = false; };
    }
    api.get<ExceptionGroup[]>('/operating-contexts/ownership-reconciliation/warehouse-transfer-exceptions/groups')
      .then(({ data }) => { if (active) setExceptionGroups(data); })
      .catch((failure: unknown) => { if (active) setExceptionError(contextError(failure)); });
    return () => { active = false; };
  }, [contexts?.canAdministerPolicy]);
  const resource = contexts?.resources.find(item => `${item.resourceType}:${item.id}` === selected);
  const groups = Array.from(new Set(contexts?.resources.flatMap(item => item.groupIds) ?? [])).sort((a, b) => a - b);
  const loadReport = async (download = false) => {
    const current = ++sequence.current;
    setBusy(true); setError(''); setReport(null);
    const params = {
      ...(resource?.resourceType === 'warehouse' ? { warehouseIds: resource.id } : {}),
      ...(group ? { groupId: Number(group) } : {}),
      onDate: date,
    };
    try {
      if (download) {
        const response = await api.get<Blob>('/operating-contexts/warehouse-report.csv', { params, responseType: 'blob' });
        if (current !== sequence.current) return;
        const url = URL.createObjectURL(response.data);
        const link = document.createElement('a');
        link.href = url; link.download = `warehouse-context-${date}.csv`; link.click();
        URL.revokeObjectURL(url);
      } else {
        const response = await api.get<Report>('/operating-contexts/warehouse-report', { params });
        if (current === sequence.current) setReport(response.data);
      }
    } catch (failure: unknown) {
      if (current === sequence.current) setError(contextError(failure));
    } finally { if (current === sequence.current) setBusy(false); }
  };
  const invalidate = () => { ++sequence.current; setReport(null); setError(''); setBusy(false); };
  const loadTransferExceptionCandidates = async (clearNotice = true) => {
    if (!exceptionGroupId) return;
    setExceptionBusy(true); setExceptionError('');
    if (clearNotice) setExceptionNotice('');
    setExceptionCandidates([]); setExceptionCandidateId(''); setExceptionConfirmation('');
    try {
      const { data } = await api.get<TransferExceptionCandidate[]>(
        '/operating-contexts/ownership-reconciliation/warehouse-transfer-exceptions/candidates',
        { params: { groupId: Number(exceptionGroupId) } });
      setExceptionCandidates(data);
    } catch (failure: unknown) {
      setExceptionError(contextError(failure));
    } finally { setExceptionBusy(false); }
  };
  const exceptionCandidate = exceptionCandidates.find(item => item.transferId === Number(exceptionCandidateId));
  const expectedExceptionConfirmation = exceptionCandidate
    ? `EXCEPT WAREHOUSE TRANSFER ${exceptionCandidate.transferId} FOR ${exceptionCandidate.groupName}` : '';
  const applyTransferException = async () => {
    if (!exceptionCandidate) return;
    setExceptionBusy(true); setExceptionError(''); setExceptionNotice('');
    try {
      await api.post('/operating-contexts/ownership-reconciliation/warehouse-transfer-exceptions', {
        groupId: exceptionCandidate.groupId,
        transferId: exceptionCandidate.transferId,
        fingerprint: exceptionCandidate.fingerprint,
        reason: exceptionReason,
        confirmation: exceptionConfirmation,
      });
      setExceptionNotice(`Виняток для переміщення #${exceptionCandidate.transferId} створено. Інші документи та склади не змінено.`);
      setExceptionReason(''); setExceptionConfirmation('');
      await loadTransferExceptionCandidates(false);
    } catch (failure: unknown) {
      setExceptionError(contextError(failure));
    } finally { setExceptionBusy(false); }
  };
  return <main className="operating-contexts">
    <h1>Операційний контекст</h1>
    <p>Лише явно дозволені сервером ресурси. Вибір контексту не надає прав. Поточний підрозділ працівника та членство груп не замінюють призначення доступу.</p>
    <button type="button" disabled={busy} onClick={() => void refresh()}>Оновити доступ</button>
    {busy && <p role="status">Завантаження…</p>}
    {error && <p role="alert">{error}</p>}
    {contexts && <>
      <p role="note">{!contexts.enforcementEnabled ? 'Контекстні обмеження ще не ввімкнено: діють попередні глобальні права.'
        : contexts.unrestricted ? 'Явний необмежений контекстний доступ; глобальні права залишаються обов’язковими.'
          : 'Контекстні обмеження ввімкнено. Порожні призначення не дають доступу.'}</p>
      {!contexts.resources.length && <p role="status">Немає доступних контекстів. Зверніться до адміністратора доступу; зміна підрозділу не відкриє доступ автоматично.</p>}
      <label>Ресурс<select aria-label="Операційний контекст" value={selected} disabled={busy}
        onChange={event => { invalidate(); setSelected(event.target.value); }}>
        <option value="">Усі дозволені ресурси</option>
        {contexts.resources.map(item => <option key={`${item.resourceType}:${item.id}`} value={`${item.resourceType}:${item.id}`}>
          {{ warehouse: 'Склад', kitchen: 'Кухня', venue: 'Зал' }[item.resourceType]}: {item.name}{item.archived ? ' (історичний/недоступний для нової роботи)' : ''}
        </option>)}
      </select></label>
      {resource?.resourceType === 'warehouse' && <Link to={`/management/inventory-balance?warehouseId=${resource.id}`}>Залишки та партії цього складу</Link>}
      {resource?.resourceType === 'kitchen' && <div className="operating-contexts__actions">
        <Link to={`/management/kitchen-work?kitchenId=${resource.id}`}>План цієї кухні</Link>
        <Link to={`/management/kitchen-execution?kitchenId=${resource.id}`}>Виконання цієї кухні</Link>
      </div>}
      {resource?.resourceType === 'venue' && <Link to={`/management/venue-bookings?venueId=${resource.id}`}>Бронювання цього залу</Link>}
      <h2>Складський звіт за контекстом</h2>
      <p>Кожен склад обчислюється незалежно. Підсумок додає тільки кількості; партії, розподіли та бази ревізій не змішуються. Спільний склад враховується один раз. Групи — позначки на дату звіту, а не історичний власник документів.</p>
      <div className="operating-contexts__actions">
        <label>Дата звіту<input type="date" value={date} disabled={busy} onChange={event => { invalidate(); setDate(event.target.value); }} /></label>
        <button type="button" disabled={busy || !date} onClick={() => void refresh(date)}>Оновити контексти на дату звіту</button>
        <label>Група на дату звіту<select value={group} disabled={busy} onChange={event => { invalidate(); setGroup(event.target.value); }}>
          <option value="">Усі дозволені склади</option>
          {groups.map(id => <option key={id} value={id}>Група #{id}</option>)}
        </select></label>
        <button type="button" disabled={busy || !date || !contexts.resources.some(item => item.resourceType === 'warehouse') || Boolean(resource && resource.resourceType !== 'warehouse')}
          onClick={() => void loadReport()}>Показати звіт</button>
        <button type="button" disabled={busy || !date || !contexts.resources.some(item => item.resourceType === 'warehouse') || Boolean(resource && resource.resourceType !== 'warehouse')}
          onClick={() => void loadReport(true)}>Експорт CSV</button>
      </div>
      {report && <div className="operating-contexts__table"><table>
        <caption>Залишки на {report.onDate}; складів: {report.warehouseIds.length}</caption>
        <thead><tr><th>Склад</th><th>Групи</th><th>Продукт</th><th>Од.</th><th>Прихід</th><th>Витрата</th><th>Залишок</th><th>Непокрите списання</th></tr></thead>
        <tbody>{report.rows.map(row => <tr key={`${row.warehouseId}:${row.productId}`}>
          <td>{row.warehouseName}</td><td>{row.groupIds.join(', ') || 'Без групи'}</td><td>{row.productName}</td><td>{row.unit}</td>
          <td>{row.stockIn}</td><td>{row.stockOut}</td><td>{row.balance}</td><td>{row.unpostedWriteOffQuantity}</td>
        </tr>)}</tbody>
        <tfoot>{report.totals.map(row => <tr key={row.productId}><th colSpan={2}>Разом, без повтору складів</th>
          <td>{row.productName}</td><td>{row.unit}</td><td>{row.stockIn}</td><td>{row.stockOut}</td><td>{row.balance}</td><td>{row.unpostedWriteOffQuantity}</td></tr>)}</tfoot>
      </table></div>}
      {contexts.canAdministerPolicy && <section className="operating-contexts__exceptions" aria-labelledby="transfer-exception-heading">
          <h2 id="transfer-exception-heading">Історичний виняток для одного переміщення</h2>
          <p>Доступний лише адміністратору політики. Виняток закріпить власника документа, але не надасть доступу до складів, залишків або інших документів.</p>
          <div className="operating-contexts__actions">
            <label>Цільова операційна група<select aria-label="Цільова група винятку" value={exceptionGroupId}
              disabled={exceptionBusy} onChange={event => {
                setExceptionGroupId(event.target.value); setExceptionCandidates([]); setExceptionCandidateId('');
                setExceptionError(''); setExceptionNotice('');
              }}>
              <option value="">Оберіть групу</option>
              {exceptionGroups.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select></label>
            <button type="button" disabled={exceptionBusy || !exceptionGroupId}
              onClick={() => void loadTransferExceptionCandidates()}>Завантажити конфліктні переміщення</button>
          </div>
          {exceptionError && <p role="alert">{exceptionError}</p>}
          {exceptionNotice && <p role="status">{exceptionNotice}</p>}
          {exceptionCandidates.length === 0 && !exceptionBusy && exceptionGroupId &&
            <p role="status">Немає відповідних непроведених винятком крос-групових документів.</p>}
          {exceptionCandidates.length > 0 && <>
            <label>Документ<select aria-label="Документ для винятку" value={exceptionCandidateId} disabled={exceptionBusy}
              onChange={event => { setExceptionCandidateId(event.target.value); setExceptionReason(''); setExceptionConfirmation(''); }}>
              <option value="">Оберіть один документ</option>
              {exceptionCandidates.map(item => <option key={item.transferId} value={item.transferId}>
                #{item.transferId} · {new Date(item.date).toLocaleDateString()} · {item.sourceWarehouseName} → {item.destinationWarehouseName}
              </option>)}
            </select></label>
            {exceptionCandidate && <>
              <p>Група-власник: {exceptionCandidate.groupName}. Склади зберігають свої поточні призначення.</p>
              <ul>{exceptionCandidate.items.map(item => <li key={`${item.productId}:${item.quantity}`}>
                {item.productName} — {item.quantity} {item.unit}
              </li>)}</ul>
              <label>Аудиторська причина<textarea aria-label="Причина винятку" maxLength={1000} value={exceptionReason}
                disabled={exceptionBusy} onChange={event => setExceptionReason(event.target.value)} /></label>
              <p>Для підтвердження введіть точно: <code>{expectedExceptionConfirmation}</code></p>
              <label>Точне підтвердження<input aria-label="Підтвердження винятку" value={exceptionConfirmation}
                disabled={exceptionBusy} onChange={event => setExceptionConfirmation(event.target.value)} /></label>
              <button type="button" disabled={exceptionBusy || !exceptionReason.trim() ||
                exceptionConfirmation !== expectedExceptionConfirmation} onClick={() => void applyTransferException()}>
                Створити виняток для цього документа
              </button>
            </>}
          </>}
      </section>}
      {contexts.canAdministerGrants && <button type="button" onClick={() => setAdministration(value => !value)}>Адміністрування доступу</button>}
      {administration && <OperatingAccessSettings onChanged={() => void refresh()} />}
    </>}
  </main>;
}
